import { makeRequest, thunkify } from '../../services/http';
import {
    ICustomerVerificationCodeResponse,
    ICustomerVerifyCodeResponse,
    ICustomersGetResponse,
    ICustomersInsuranceManualResponse,
    ICustomersInsuranceUploadResponse,
    ICustomersLoginResponse,
    ICustomersUserOnboardingResponse,
    ICustomersMonographResponse,
    ICustomersValidateResponse,
    ICustomersTestInsuranceResponse,
    ICreditCard,
    ICodeDataResponse,
    IRefillConsent,
    IRefillConsentApproveResponse,
    IRefillConsentApprovePayload,
    INewPrescriptionConsentPayload,
    IChangeGenerateCodeResponse,
    IChangeContactValidateResponse,
    IChangeContactUpdateResponse
} from './models/response';
import {
    ICustomersCreateInsuranceRequest,
    ICustomersDeleteInsuranceRequest,
    ICustomersLoginRequest,
    ICustomersMetadataRequest,
    ICustomersVerificationCodeRequest,
    ICustomersRegisterRequest,
    ICustomerVerifyCodeRequest,
    ICustomersVerificationCodeVerifyingRequest,
    AuthCardCreateRequest,
    ICustomerDeletePaymentMethodRequest,
    ICustomerUpdatePreferredPaymentMethodRequest,
    ICustomerTestInsuranceRequestModel,
    ICustomersTestInsuranceV2Request,
    ICustomersValidateRequest,
    ICustomersGenerateIdentityCodeRequest,
    ICustomersVerifyIdentityCodeRequest,
    ICustomersSetPasswordRequest,
    ICustomersCodeByAuthRequest,
    ICustomersVerifyByAuthRequest,
    ICustomersInvoiceChargeRequest,
    IBillingDetailsRequest,
    IUpdateCreditCardBillingAddressRequest,
    IContactUpdateRequest,
    IContactDeleteRequest,
    IContactCreateRequest,
    IConfirmDeliveryRequest,
    IValidatePasswordRequest,
    IValidateEmailAvailabilityAndPasswordRequest,
    IChangeGenerateCodeRequest,
    IVerifyOtpRequest,
    IChangeEmailVerifyAndUpdateRequest,
    IChangePhoneVerifyAndUpdateRequest,
    IChangePasswordVerifyAndUpdateRequest
} from './models/request';
import { ITransferPrescriptions } from '@/shared/types/interfaces/transfer-prescriptions';
import { IContactResponse } from './models/response';
import { IDeliveryTicket, IDeliveryPackage, IInvoice, IPendingPrescription, ILoginErrorResponse } from '@/shared/types/interfaces';
import { ICustomerRequestItem, ICustomerRequestItems, ICustomerRequestPayload } from '@/shared/types/interfaces/customer-requests';
import { toast } from 'react-toastify';
import _ from 'lodash';

const controllerName = 'customers';
export const queryParamsForPAT = [
    'ERX_REFILL_REQUEST_PENDING',
    'FAX_REFILL_REQUEST_PENDING',
    'ERX_NEWRX_REQUEST_PENDING',
    'FAX_NEWRX_REQUEST_PENDING',
    'FAX_PRESCRIPTION_REQUEST_PENDING'
];

export class CustomersController {
    static get = thunkify<ICustomersGetResponse>('get', controllerName, '', async (request, data) => request(data));

    static login = thunkify<ICustomerVerifyCodeResponse, ICustomersLoginRequest, ILoginErrorResponse>(
        'post',
        controllerName,
        'login',
        async (request, data) =>
            request(data, {
                formUrlencoded: true,
                headers: {
                    Accept: 'application/com.quickfill.customerapp-v3.1+json'
                }
            })
    );

    static register = thunkify<ICustomerVerifyCodeResponse, ICustomersRegisterRequest>(
        'post',
        controllerName,
        'register',
        async (request, data) =>
            request(data, {
                formUrlencoded: false,
                headers: {
                    Accept: 'application/com.quickfill.customerapp-v3.1+json'
                }
            })
    );

    static confirmCode = thunkify<ICustomerVerifyCodeResponse, ICustomerVerifyCodeRequest>(
        'post',
        controllerName,
        'verify?noErrorLog',
        async (request, data) =>
            request(data, {
                formUrlencoded: true,
                headers: {
                    Accept: 'application/com.quickfill.customerapp-v3.1+json'
                }
            })
    );

    static logout = thunkify<ICustomersLoginResponse>('post', controllerName, 'logout');

    static getCodeData = thunkify<ICodeDataResponse>('get', controllerName, 'code-data');

    static generateVerificationCode = thunkify<ICustomerVerificationCodeResponse, ICustomersVerificationCodeRequest>(
        'post',
        controllerName,
        'generate/code',
        async (request, data) => {
            return request(data, {
                formUrlencoded: true,
                headers: {
                    Accept: 'application/com.quickfill.customerapp-v3.1+json',
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
        }
    );

    static generateIdentityCode = thunkify<ICustomerVerificationCodeResponse, ICustomersGenerateIdentityCodeRequest>(
        'post',
        controllerName,
        'generate/code-by-identity',
        async (request, data) =>
            request(data, {
                formUrlencoded: true,
                headers: {
                    Accept: 'application/com.quickfill.customerapp-v3.1+json',
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            })
    );

    static codeByAuth = thunkify<ICustomerVerificationCodeResponse, ICustomersCodeByAuthRequest>(
        'post',
        controllerName,
        'generate/code-by-auth',
        async (request, data) => {
            const { authToken, ...rest } = data;
            return request(rest, {
                formUrlencoded: true,
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    Accept: 'application/com.quickfill.customerapp-v3.1+json',
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
        }
    );

    static verifyByAuth = thunkify<ICustomerVerificationCodeResponse, ICustomersVerifyByAuthRequest>(
        'post',
        controllerName,
        'verify-by-auth',
        async (request, data) => {
            const { authToken, ...rest } = data;
            return request(rest, {
                formUrlencoded: true,
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    Accept: 'application/com.quickfill.customerapp-v3.1+json',
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
        }
    );

    static verifyIdentityCode = thunkify<ICustomersLoginResponse, ICustomersVerifyIdentityCodeRequest>(
        'post',
        controllerName,
        'verify-by-identity',
        async (request, data) =>
            request(data, {
                formUrlencoded: true,
                headers: {
                    Accept: 'application/com.quickfill.customerapp-v3.1+json',
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            })
    );

    static updateDeliveryTicketAddress = thunkify<IDeliveryTicket, { userId: number; addressId: number; ver: number; id: number }>(
        'patch',
        controllerName,
        ':userId/delivery-tickets/:ticketId/address',
        async (request, data) =>
            request(
                {
                    addressType: 'CUSTOMER',
                    ver: data.ver,
                    id: data.id,
                    addressId: data.addressId
                },
                {
                    urlParams: {
                        userId: data.userId,
                        ticketId: data.id
                    },
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            )
    );
    // Accept: 'application/com.quickfill.customerapp-v3.1+json',

    static setPassword = thunkify<null, ICustomersSetPasswordRequest>('patch', controllerName, 'set-password', async (request, data) =>
        request(_.omit(data, 'token'), {
            formUrlencoded: true,
            headers: {
                Authorization: `Bearer ${data.token}`,
                Accept: 'application/com.quickfill.customerapp-v3.1+json',
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        })
    );

    static validate = thunkify<ICustomersValidateResponse, Partial<ICustomersValidateRequest>>(
        'post',
        controllerName,
        'validate',
        async (request, data) =>
            request(data, {
                formUrlencoded: true,
                headers: {
                    Accept: 'application/com.quickfill.customerapp-v3.1+json',
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            })
    );

    static verifyVerificationCode = thunkify<ICustomerVerifyCodeResponse, ICustomersVerificationCodeVerifyingRequest>(
        'post',
        controllerName,
        'verify',
        async (request, data) => request(data, { formUrlencoded: true })
    );

    static refillRequest = thunkify<null, { id: number }>('post', controllerName, 'prescription/:id/refill', (request, data) => {
        return request(
            {},
            {
                urlParams: { id: data.id },
                params: {}
            }
        );
    });

    static eligibility = thunkify<ICustomersInsuranceManualResponse[]>('post', controllerName, 'eligibility');

    static insuranceCreate = thunkify<ICustomersInsuranceManualResponse, ICustomersCreateInsuranceRequest>(
        'post',
        controllerName,
        'insurances',
        undefined
    );

    static insuranceUpload = thunkify<ICustomersInsuranceUploadResponse, FormData>('post', controllerName, 'insurance-upload');

    static signatureUpload = thunkify<ICustomersInsuranceUploadResponse, FormData>(
        'post',
        controllerName,
        'upload-documents?documentType=TRANSFER_AUTH_CUSTOMER_SIGNATURE'
    );

    static testInsuranceV2 = thunkify<ICustomersTestInsuranceResponse, ICustomersTestInsuranceV2Request>(
        'post',
        controllerName,
        'test-insurance'
    );

    static testInsurance = thunkify<ICustomersTestInsuranceResponse, ICustomerTestInsuranceRequestModel>(
        'post',
        controllerName,
        'insurances/:id/test-insurance',
        (request, data) => {
            return request(data, { urlParams: { id: data.id } });
        }
    );

    static requestRefill = thunkify<any, any>('post', controllerName, 'prescription/:id/refill', (request, data) => {
        return request(data, { urlParams: { id: data.id } });
    });

    static requestRefillPharmTask = thunkify<any, any>('post', controllerName, 'customer-requests/:id/refill', (request, data) => {
        return request(data, { urlParams: { id: data.id } });
    });

    static insuranceDelete = thunkify<null, ICustomersDeleteInsuranceRequest>(
        'delete',
        controllerName,
        'insurances/:id',
        (request, data) => {
            return request({
                urlParams: { id: data.id },
                params: { version: data.version }
            });
        }
    );

    static addPaymentMethod = thunkify<ICreditCard, AuthCardCreateRequest>('post', controllerName, 'credit-cards');

    static paymentMethodDelete = thunkify<null, ICustomerDeletePaymentMethodRequest>(
        'delete',
        controllerName,
        'credit-cards/:id',
        (request, data) => {
            return request({
                urlParams: { id: data.id },
                params: { version: data.ver }
            });
        }
    );

    static paymentMethodUpdatePreferred = thunkify<ICreditCard, ICustomerUpdatePreferredPaymentMethodRequest>(
        'patch',
        controllerName,
        'credit-cards/:id/preferred',
        (request, data) => {
            return request(data, {
                urlParams: { id: data.id }
            });
        }
    );

    static updateCreditCardBillingAddress = thunkify<IBillingDetailsRequest, IUpdateCreditCardBillingAddressRequest>(
        'patch',
        controllerName,
        'credit-cards/billing-address',
        (request, data) => request(data)
    );

    static insuranceUploadDelete = thunkify<null, ICustomersDeleteInsuranceRequest>(
        'delete',
        controllerName,
        'insurance-uploads/:id',
        (request, data) => {
            return request({
                urlParams: { id: data.id },
                params: { version: data.version }
            });
        }
    );

    static addTransferPrescription = thunkify<ITransferPrescriptions | null, ITransferPrescriptions>(
        'post',
        controllerName,
        'transfer-prescriptions',
        async (request, data) => {
            let res: ITransferPrescriptions | null;
            try {
                res = await request(data);
                return res;
            } catch (error) {
                toast.error('Failed to add medication');
                return null;
            }
        }
    );

    static getMonograph = (ndc: string) => {
        return makeRequest<ICustomersMonographResponse[]>('get', controllerName, 'monograph/:ndc/en', { urlParams: { ndc } });
    };

    static getOtcPriceByQuantityMedId = thunkify<string, { medid: string; quantity?: string }>(
        'post',
        'prescribables',
        ':medid/estimated-price',
        (request, data) => {
            return request(
                {},
                {
                    urlParams: { medid: data.medid },
                    params: {
                        quantity: data.quantity
                    },
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/x-www-form-urlencoded'
                    }
                }
            );
        }
    );

    static updateOtcQuantityPackaging = thunkify<ITransferPrescriptions, ITransferPrescriptions>(
        'patch',
        controllerName,
        'transfer-prescriptions/:transfer_prescription_id',
        async (request, data) => {
            return await request(data, {
                urlParams: { transfer_prescription_id: data.id }
            });
        }
    );

    static removeTransferPrescription = thunkify<number | string, ITransferPrescriptions>(
        'delete',
        controllerName,
        'transfer-prescriptions/:id',
        async (request, data) => {
            await request({
                urlParams: { id: data.id },
                params: { version: data.ver }
            });

            return data.id;
        }
    );

    static updateMetadata = thunkify<
        ICustomersUserOnboardingResponse,
        ICustomersMetadataRequest<ICustomersUserOnboardingResponse['details']>
    >('put', controllerName, 'onboarding/:id', (request, data) => {
        return request(data, {
            urlParams: { id: data.id }
        });
    });

    static onFinishOnboarding = thunkify<unknown, void>('post', controllerName, 'complete-onboarding', async (request, data) =>
        request(data, { formUrlencoded: false })
    );

    static getPrescriptions = thunkify<ITransferPrescriptions[]>(
        'get',
        controllerName,
        'prescriptions?active=false', // we pass active false to get all the prescriptions
        async request => await request()
    );

    static getPendingPrescriptions = thunkify<IPendingPrescription[]>(
        'get',
        controllerName,
        `prescription-action-trackings?${queryParamsForPAT.map(param => `type=${param}`).join('&')}`,
        async request => await request()
    );

    static getInvoices = thunkify<IInvoice[], string | undefined>(
        'get',
        controllerName,
        'invoices',
        async (request, data) => await request(data)
    );

    static chargeInvoice = thunkify<IInvoice, ICustomersInvoiceChargeRequest>(
        'post',
        controllerName,
        'invoices/:id/charge',
        async (request, data) =>
            await request(data, {
                urlParams: { id: data.id }
            })
    );

    static getContacts = thunkify<IContactResponse[]>('get', controllerName, 'contacts', async request => await request());

    static getDeliveryTickets = thunkify<IDeliveryTicket[]>('get', controllerName, 'delivery-tickets', async request => await request());

    static confirmDelivery = thunkify<IDeliveryPackage, IConfirmDeliveryRequest>(
        'post',
        controllerName,
        'delivery-tickets/:ticketId/delivery-packages/:packageId/confirm-delivery',
        async (request, data) =>
            await request(
                {
                    base64Signature: data.base64Signature,
                    ver: data.ver,
                    contentType: data.contentType
                },
                {
                    urlParams: {
                        ticketId: data.ticketId,
                        packageId: data.packageId
                    },
                    formUrlencoded: true
                }
            )
    );

    static getRefillConsents = thunkify<IRefillConsent[]>('get', controllerName, 'refill-consent?states=REQUESTED', async request => await request());
    static approveRefillConsents = thunkify<IRefillConsentApproveResponse[], IRefillConsentApprovePayload[]>(
        'put',
        controllerName,
        'refill-consent',
        async (request, data) => await request(data)
    );
    static approveNewPrescriptionConsent = thunkify<IRefillConsentApproveResponse, INewPrescriptionConsentPayload>(
        'put',
        controllerName,
        'refill-consent/new-prescription-response',
        async (request, data) => await request(data)
    );

    static getCustomerRequests = thunkify<ICustomerRequestItems>('get', controllerName, 'requests', async request => await request());
    static addCustomerRequest = thunkify<ICustomerRequestItem, ICustomerRequestPayload>(
        'post',
        controllerName,
        'requests',
        async (request, data) => await request(data)
    );

    static updateContact = thunkify<IContactResponse, IContactUpdateRequest>(
        'put',
        controllerName,
        'contacts/:id',
        async (request, data) =>
            await request(data, {
                urlParams: { id: data.id }
            })
    );

    static deleteContact = thunkify<null, IContactDeleteRequest>('delete', controllerName, 'contacts/:id', (request, data) => {
        return request({
            urlParams: { id: data.id },
            params: { version: data.ver },
            data: data.body,
        });
    });

    static createContact = thunkify<IContactResponse, IContactCreateRequest>(
        'post',
        controllerName,
        'contacts',
        async (request, data) => await request(data)
    );
        
    static validatePassword = thunkify<IChangeContactValidateResponse, IValidatePasswordRequest>(
        'post',
        controllerName,
        'validate-password',
        async (request, data) => request(data, { formUrlencoded: true })
    );

    static validateEmailAvailabilityAndPassword = thunkify<IChangeContactValidateResponse, IValidateEmailAvailabilityAndPasswordRequest>(
        'post',
        controllerName,
        'validate-email-availability-and-validate-password',
        async (request, data) => request(data, { formUrlencoded: true })
    );

    static changeEmailGenerateCode = thunkify<IChangeGenerateCodeResponse, IChangeGenerateCodeRequest>(
        'post',
        controllerName,
        'change-email/generate-code',
        async (request, data) => request(data, { formUrlencoded: true })
    );

    static changePhoneGenerateCode = thunkify<IChangeGenerateCodeResponse, IChangeGenerateCodeRequest>(
        'post',
        controllerName,
        'change-phone-number/generate-code',
        async (request, data) => request(data, { formUrlencoded: true })
    );

    static changePasswordGenerateCode = thunkify<IChangeGenerateCodeResponse, IChangeGenerateCodeRequest>(
        'post',
        controllerName,
        'change-password/generate-code',
        async (request, data) => request(data, { formUrlencoded: true })
    );

    static verifyOtp = thunkify<IChangeContactUpdateResponse, IVerifyOtpRequest>(
        'post',
        controllerName,
        'verify-otp',
        async (request, data) => request(data, { formUrlencoded: true })
    );

    static changeEmailVerifyAndUpdate = thunkify<IChangeContactUpdateResponse, IChangeEmailVerifyAndUpdateRequest>(
        'patch',
        controllerName,
        'change-email/verify-otp-and-update-email',
        async (request, data) => request(data, { formUrlencoded: true })
    );

    static changePhoneVerifyAndUpdate = thunkify<IChangeContactUpdateResponse, IChangePhoneVerifyAndUpdateRequest>(
        'patch',
        controllerName,
        'change-phone-number/verify-otp-and-update-phone-number',
        async (request, data) => request(data, { formUrlencoded: true })
    );

    static changePasswordVerifyAndUpdate = thunkify<IChangeContactUpdateResponse, IChangePasswordVerifyAndUpdateRequest>(
        'patch',
        controllerName,
        'change-password/verify-otp-and-update-password',
        async (request, data) => request(data, { formUrlencoded: true })
    );
}

export * from './models/request';
export * from './models/response';
export * from './models/enums';
